import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { DatePickerField } from "./DatePickerField";
import { AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { formatInvoiceAmount, SHORTFALL_REASONS, getShortfall, getShortfallReasonLabel } from "@/lib/invoiceUtils";

interface Invoice {
  id: string;
  installment_number: number;
  percentage: number;
  amount: number;
  due_date: string | null;
  invoice_number: string | null;
  status: string;
  issued_at: string | null;
  paid_at: string | null;
  payment_reference: string | null;
  correction_count?: number;
  amount_received?: number | null;
  shortfall_reason?: string | null;
  shortfall_notes?: string | null;
}

interface EditInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Invoice | null;
  leadId: string;
  currency: string;
}

export const EditInvoiceDialog = ({
  open,
  onOpenChange,
  invoice,
  leadId,
  currency,
}: EditInvoiceDialogProps) => {
  const queryClient = useQueryClient();
  
  const [amount, setAmount] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [status, setStatus] = useState("");
  const [paidAt, setPaidAt] = useState<Date | undefined>(undefined);
  const [paymentReference, setPaymentReference] = useState("");
  const [correctionReason, setCorrectionReason] = useState("");
  const [amountReceived, setAmountReceived] = useState<string>("");
  const [shortfallReason, setShortfallReason] = useState<string>("");
  const [shortfallNotes, setShortfallNotes] = useState<string>("");
  const [revertLeadStatus, setRevertLeadStatus] = useState(true);

  const { data: leadInfo } = useQuery({
    queryKey: ["lead-travel-date", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("status, start_date")
        .eq("id", leadId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const isRevertingPaid = invoice?.status === "paid" && status !== "paid";
  const isFutureTravel = (() => {
    if (!leadInfo?.start_date) return false;
    const sixMonthsOut = new Date();
    sixMonthsOut.setMonth(sixMonthsOut.getMonth() + 6);
    return new Date(leadInfo.start_date) > sixMonthsOut;
  })();

  // Reset form when invoice changes
  useEffect(() => {
    if (invoice) {
      setAmount(invoice.amount.toString());
      setInvoiceNumber(invoice.invoice_number || "");
      setStatus(invoice.status);
      setPaidAt(invoice.paid_at ? new Date(invoice.paid_at) : undefined);
      setPaymentReference(invoice.payment_reference || "");
      setCorrectionReason("");
      setAmountReceived(invoice.amount_received != null ? invoice.amount_received.toString() : invoice.amount.toString());
      setShortfallReason(invoice.shortfall_reason || "");
      setShortfallNotes(invoice.shortfall_notes || "");
    }
  }, [invoice]);

  const correctionMutation = useMutation({
    mutationFn: async () => {
      if (!invoice) throw new Error("No invoice selected");
      if (!correctionReason.trim()) throw new Error("Correction reason is required");

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const newAmount = parseFloat(amount);
      const newInvoiceNumber = invoiceNumber.trim() || null;
      const newPaidAt = paidAt?.toISOString() || null;
      const newPaymentRef = paymentReference.trim() || null;
      const newReceived = amountReceived === "" ? null : parseFloat(amountReceived);
      const sf = newReceived != null ? getShortfall(newAmount, newReceived) : 0;
      if (sf > 0 && !shortfallReason) throw new Error("Select a shortfall reason");
      if (sf > 0 && shortfallReason === 'other' && !shortfallNotes.trim()) throw new Error("Add shortfall notes");
      const newShortfallReason = sf > 0 ? shortfallReason : null;
      const newShortfallNotes = sf > 0 && shortfallNotes.trim() ? shortfallNotes.trim() : null;

      // Insert correction record
      const { error: correctionError } = await supabase
        .from("invoice_corrections")
        .insert({
          invoice_id: invoice.id,
          corrected_by: user.id,
          correction_reason: correctionReason.trim(),
          old_amount: invoice.amount,
          new_amount: newAmount,
          old_invoice_number: invoice.invoice_number,
          new_invoice_number: newInvoiceNumber,
          old_paid_at: invoice.paid_at,
          new_paid_at: newPaidAt,
          old_payment_reference: invoice.payment_reference,
          new_payment_reference: newPaymentRef,
          old_status: invoice.status,
          new_status: status,
          old_amount_received: invoice.amount_received ?? null,
          new_amount_received: newReceived,
          old_shortfall_reason: invoice.shortfall_reason ?? null,
          new_shortfall_reason: newShortfallReason,
          old_shortfall_notes: invoice.shortfall_notes ?? null,
          new_shortfall_notes: newShortfallNotes,
        });

      if (correctionError) throw correctionError;

      // Update the invoice
      const { error: updateError } = await supabase
        .from("invoices")
        .update({
          amount: newAmount,
          invoice_number: newInvoiceNumber,
          status: status as "pending" | "issued" | "paid",
          paid_at: newPaidAt,
          payment_reference: newPaymentRef,
          correction_count: (invoice.correction_count || 0) + 1,
          amount_received: newReceived,
          shortfall_reason: newShortfallReason,
          shortfall_notes: newShortfallNotes,
        })
        .eq("id", invoice.id);

      if (updateError) {
        if (updateError.code === "23505" && newInvoiceNumber) {
          const { data: owner } = await supabase
            .from("invoices")
            .select("lead_id, leads(client_name)")
            .eq("invoice_number", newInvoiceNumber)
            .neq("id", invoice.id)
            .maybeSingle();
          const ownerName = (owner as any)?.leads?.client_name;
          throw new Error(
            `Invoice number ${newInvoiceNumber} is already in use${ownerName ? ` on the "${ownerName}" lead` : ""}. Release it there first, or choose a different number.`
          );
        }
        throw updateError;
      }

      // Reverting a paid invoice: optionally roll the lead back out of execution
      if (invoice.status === "paid" && status !== "paid" && revertLeadStatus) {
        const { data: revertedLead } = await supabase
          .from("leads")
          .update({ status: "invoicing" })
          .eq("id", leadId)
          .eq("status", "executing")
          .select("id");

        await supabase
          .from("coffeebeans_bookings")
          .update({ payment_status: "invoice_pending" })
          .eq("lead_id", leadId)
          .eq("payment_status", "paid");

        if (revertedLead && revertedLead.length > 0) {
          await supabase.from("lead_changes").insert({
            lead_id: leadId,
            user_id: user.id,
            field_changed: "status",
            old_value: "executing",
            new_value: "invoicing",
            change_source: `Invoice #${invoice.invoice_number || invoice.installment_number} payment reverted`,
          });
        }

        await supabase.from("activity_log").insert({
          lead_id: leadId,
          user_id: user.id,
          action: "Payment Reverted",
          details: `Invoice #${invoice.invoice_number || invoice.installment_number} reverted from paid to ${status}. Lead returned to invoicing and linked bookings marked as awaiting payment.`,
        });
      }

      // Log activity
      const changes: string[] = [];
      if (invoice.amount !== newAmount) {
        changes.push(`Amount: ${formatInvoiceAmount(invoice.amount, currency)} → ${formatInvoiceAmount(newAmount, currency)}`);
      }
      if ((invoice.invoice_number || null) !== newInvoiceNumber) {
        changes.push(`Invoice number: ${invoice.invoice_number || '—'} → ${newInvoiceNumber || '— (released)'}`);
      }
      if (invoice.status !== status) {
        changes.push(`Status: ${invoice.status} → ${status}`);
      }
      if (invoice.paid_at !== newPaidAt) {
        changes.push(`Payment date updated`);
      }
      if (invoice.payment_reference !== newPaymentRef) {
        changes.push(`Payment reference updated`);
      }
      if ((invoice.amount_received ?? null) !== newReceived) {
        changes.push(`Amount received: ${invoice.amount_received != null ? formatInvoiceAmount(invoice.amount_received, currency) : '—'} → ${newReceived != null ? formatInvoiceAmount(newReceived, currency) : '—'}`);
      }
      if ((invoice.shortfall_reason ?? null) !== newShortfallReason) {
        changes.push(`Shortfall reason: ${getShortfallReasonLabel(invoice.shortfall_reason) || '—'} → ${getShortfallReasonLabel(newShortfallReason) || '—'}`);
      }

      await supabase.from("activity_log").insert({
        lead_id: leadId,
        user_id: user.id,
        action: "Invoice Corrected",
        details: `Invoice #${invoice.invoice_number || invoice.installment_number} corrected. Changes: ${changes.join("; ")}. Reason: ${correctionReason.trim()}`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices", leadId] });
      queryClient.invalidateQueries({ queryKey: ["activity-log", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead-changes", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead-travel-date", leadId] });
      toast.success("Invoice corrected successfully");
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast.error(`Failed to correct invoice: ${error.message}`);
    },
  });

  const hasChanges = invoice && (
    parseFloat(amount) !== invoice.amount ||
    (invoiceNumber.trim() || null) !== (invoice.invoice_number || null) ||
    status !== invoice.status ||
    (paidAt?.toISOString() || null) !== invoice.paid_at ||
    (paymentReference.trim() || null) !== invoice.payment_reference ||
    (amountReceived === "" ? null : parseFloat(amountReceived)) !== (invoice.amount_received ?? null) ||
    (shortfallReason || null) !== (invoice.shortfall_reason ?? null) ||
    (shortfallNotes.trim() || null) !== (invoice.shortfall_notes ?? null)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Correct Invoice
          </DialogTitle>
          <DialogDescription>
            Making corrections to invoice #{invoice?.invoice_number || invoice?.installment_number}.
            All changes are audited.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="invoice-number">Invoice Number</Label>
            <div className="flex gap-2">
              <Input
                id="invoice-number"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Not assigned"
              />
              {invoice?.invoice_number && invoiceNumber === invoice.invoice_number && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => setInvoiceNumber("")}
                >
                  Release
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Releasing frees the number so it can be issued on another lead.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Amount ({currency})</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            {invoice && parseFloat(amount) !== invoice.amount && (
              <p className="text-xs text-muted-foreground">
                Original: {formatInvoiceAmount(invoice.amount, currency)}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {status === "paid" && invoice?.status !== "paid" && isFutureTravel && leadInfo?.start_date && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Notice: travel date is {format(new Date(leadInfo.start_date), "d MMM yyyy")} — more than 6 months away. Confirm this payment is intended for a future booking.
              </span>
            </div>
          )}

          {isRevertingPaid && (
            <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={revertLeadStatus}
                onChange={(e) => setRevertLeadStatus(e.target.checked)}
                className="mt-1 rounded border-gray-300"
              />
              <span className="text-sm text-amber-900">
                Also move the lead back from Executing to Invoicing and mark linked bookings as awaiting payment.
              </span>
            </label>
          )}

          {(status === "paid" || invoice?.status === "paid") && (
            <>
              <DatePickerField
                label="Payment Date"
                value={paidAt}
                onChange={(date) => setPaidAt(date || undefined)}
              />

              <div className="space-y-2">
                <Label htmlFor="payment-reference">Payment Reference</Label>
                <Input
                  id="payment-reference"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Payment confirmation number"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount-received">Amount Received ({currency})</Label>
                <Input
                  id="amount-received"
                  type="number"
                  step="0.01"
                  value={amountReceived}
                  onChange={(e) => setAmountReceived(e.target.value)}
                />
              </div>
              {(() => {
                const newAmt = parseFloat(amount);
                const recv = amountReceived === '' ? null : parseFloat(amountReceived);
                const sf = recv != null && !isNaN(newAmt) ? getShortfall(newAmt, recv) : 0;
                if (sf <= 0) return null;
                return (
                  <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3">
                    <p className="text-sm text-amber-800">
                      Shortfall: {formatInvoiceAmount(sf, currency)}
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="shortfall-reason">Reason for shortfall *</Label>
                      <Select value={shortfallReason} onValueChange={setShortfallReason}>
                        <SelectTrigger id="shortfall-reason">
                          <SelectValue placeholder="Select reason" />
                        </SelectTrigger>
                        <SelectContent>
                          {SHORTFALL_REASONS.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {shortfallReason === 'other' && (
                      <div className="space-y-2">
                        <Label htmlFor="shortfall-notes">Notes *</Label>
                        <Textarea
                          id="shortfall-notes"
                          value={shortfallNotes}
                          onChange={(e) => setShortfallNotes(e.target.value)}
                          placeholder="Explain the shortfall"
                        />
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="reason" className="text-destructive">
              Correction Reason *
            </Label>
            <Textarea
              id="reason"
              value={correctionReason}
              onChange={(e) => setCorrectionReason(e.target.value)}
              placeholder="Explain why this correction is being made..."
              className="min-h-[80px]"
            />
            <p className="text-xs text-muted-foreground">
              Required. This reason will be recorded in the audit trail.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => correctionMutation.mutate()}
            disabled={!correctionReason.trim() || !hasChanges || correctionMutation.isPending}
          >
            {correctionMutation.isPending ? "Saving..." : "Save Correction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
